import { createFileRoute, Link } from "@tanstack/react-router";
import { getErrorMessage } from "@/lib/errorMessage";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { ArrowLeft, ChevronDown, PackageX, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  getMyRequestTimeline,
  getMyRequests,
  isOpenStatus,
  REQUEST_STATUS_HELP,
  requestStatusLabel,
  type CustomerRequest,
} from "@/lib/support/requests.functions";

export const Route = createFileRoute("/_authenticated/account_/requests")({
  head: () => ({
    meta: [
      { title: "Your requests · TruWe" },
      { name: "description", content: "Track your TruWe return, refund and cancellation requests." },
      { name: "robots", content: "noindex" },
    ],
  }),
  errorComponent: ({ error, reset }: { error: unknown; reset: () => void }) => (
    <div className="mx-auto max-w-3xl px-4 py-12">
      <h1 className="font-display text-2xl font-semibold">Couldn't load your requests</h1>
      <p className="mt-2 text-sm text-muted-foreground">{getErrorMessage(error)}</p>
      <Button className="mt-4 rounded-full" onClick={reset}>
        Try again
      </Button>
    </div>
  ),
  component: RequestsPage,
});

function statusTone(status: string): string {
  if (status === "rejected") return "bg-destructive/10 text-destructive";
  if (status === "resolved" || status === "approved") return "bg-emerald-50 text-emerald-700";
  if (status === "waiting_customer") return "bg-amber-50 text-amber-700";
  if (isOpenStatus(status)) return "bg-brand-soft/70 text-brand-deep";
  return "bg-muted text-muted-foreground";
}

function fmt(d: string) {
  return new Date(d).toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function RequestsPage() {
  const listFn = useServerFn(getMyRequests);
  const requests = useQuery({
    queryKey: ["account", "requests"],
    queryFn: () => listFn(),
    staleTime: 20_000,
  });

  const rows = requests.data?.requests ?? [];
  const openCount = rows.filter((r) => isOpenStatus(r.status)).length;

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <Link
        to="/account"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Back to account
      </Link>

      <h1 className="mt-4 font-display text-3xl font-semibold tracking-tight">Your requests</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Track every return, refund and cancellation request in one place.
        {openCount > 0 && (
          <>
            {" "}
            <span className="font-medium text-brand-deep">
              {openCount} open request{openCount === 1 ? "" : "s"}.
            </span>
          </>
        )}
      </p>

      <div className="mt-4">
        <Link
          to="/account/refunds"
          search={{ order: undefined }}
          className="inline-flex items-center gap-1.5 rounded-full border border-border px-3.5 py-1.5 text-sm font-medium hover:border-brand hover:text-brand"
        >
          <RotateCcw size={14} /> Raise a return / refund
        </Link>
      </div>

      <section className="mt-8">
        {requests.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading your requests…</p>
        ) : !rows.length ? (
          <div className="rounded-2xl border border-dashed border-border p-8 text-center">
            <PackageX className="mx-auto h-6 w-6 text-muted-foreground" />
            <p className="mt-3 text-sm text-muted-foreground">
              You haven't raised any requests yet.
            </p>
          </div>
        ) : (
          <ul className="space-y-4">
            {rows.map((r) => (
              <RequestCard key={r.id} request={r} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function RequestCard({ request: r }: { request: CustomerRequest }) {
  const [open, setOpen] = useState(false);
  const timelineFn = useServerFn(getMyRequestTimeline);
  const timeline = useQuery({
    queryKey: ["account", "request-timeline", r.id],
    queryFn: () => timelineFn({ data: { ticketId: r.id } }),
    enabled: open,
    staleTime: 20_000,
  });

  return (
    <li className="overflow-hidden rounded-2xl border border-border bg-card">
      <div className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <span className="rounded-full bg-muted px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
              {r.kind === "refund" ? "Return / refund" : "Cancellation"}
            </span>
            <p className="mt-2 truncate text-sm font-semibold text-brand-deep">
              {r.ticketNumber}
              {r.orderName ? ` · ${r.orderName}` : ""}
            </p>
          </div>
          <span
            className={`rounded-full px-2.5 py-1 text-xs font-medium ${statusTone(r.status)}`}
          >
            {requestStatusLabel(r.status)}
          </span>
        </div>

        <p className="mt-1 text-xs text-muted-foreground">
          Raised {fmt(r.createdAt)}
          {r.evidenceCount > 0 && ` · ${r.evidenceCount} photo${r.evidenceCount === 1 ? "" : "s"}`}
        </p>

        <p className="mt-2 text-sm text-muted-foreground">
          {REQUEST_STATUS_HELP[r.status] ?? "We'll keep you posted on WhatsApp."}
        </p>

        {!!r.items.length && (
          <p className="mt-3 text-sm">
            {r.items.map((i) => `${i.title} ×${i.quantity}`).join(", ")}
          </p>
        )}
        {r.reasonLabel && (
          <p className="mt-1 text-sm text-muted-foreground">Reason: {r.reasonLabel}</p>
        )}
        {r.description && (
          <p className="mt-1 text-sm text-muted-foreground">{r.description}</p>
        )}
        {r.resolutionNotes && (
          <p className="mt-3 rounded-xl bg-muted/50 p-3 text-sm">
            <span className="font-medium">Update from TruWe: </span>
            {r.resolutionNotes}
          </p>
        )}

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:text-brand-deep"
        >
          {open ? "Hide" : "View"} status history
          <ChevronDown size={14} className={open ? "rotate-180 transition" : "transition"} />
        </button>
      </div>

      {open && (
        <div className="border-t border-border/60 bg-muted/20 px-5 py-4">
          {timeline.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading history…</p>
          ) : !timeline.data?.events.length ? (
            <p className="text-sm text-muted-foreground">No updates yet.</p>
          ) : (
            <ol className="space-y-3">
              {timeline.data.events.map((e) => (
                <li key={e.id} className="flex gap-3">
                  <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-brand-deep">{e.label}</p>
                    <p className="text-xs text-muted-foreground">{fmt(e.at)}</p>
                    {e.note && <p className="mt-1 text-sm text-muted-foreground">{e.note}</p>}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </li>
  );
}
